import { Inject, Injectable, Optional } from '@angular/core';
import { Observable, of, throwError } from 'rxjs';
import { catchError, delay, finalize, map } from 'rxjs/operators';
import { PeopleSdk } from 'lib-klab-sdks';
import { AppEventsService } from '../../events/products-events.service';
import { CoreUIToastDirection, CoreUIToastV2Service } from '../../services/toast.service';
import { EmployeeHierarchiesPort } from '../../../app/ports/people/employee-hierarchies.port';
import { CrudMockAction } from '../../config/crud-mock-action.type';
import { EnvMicroPeople } from '../../config/env.micro.people';
import { PEOPLE_ENV } from '../../config/micros-env.tokens';
import { employeeHierarchiesSdkMock } from '../../mocks/people/employee-hierarchies.mock';

type EmployeeHierarchiesMockAction = Exclude<CrudMockAction, 'getById'> | 'getEmployeesByManager';

@Injectable({ providedIn: 'root' })
export class EmployeeHierarchiesPortAdapter implements EmployeeHierarchiesPort {
  constructor(
    private employeeHierarchiesApi: PeopleSdk.EmployeeHierarchiesService,
    private appEvents: AppEventsService,
    @Optional() private toast: CoreUIToastV2Service,
    @Inject(PEOPLE_ENV) private env: EnvMicroPeople,
  ) { }

  getAllEmployeeHierarchies(
    page?: number,
    size?: number,
    locationId?: number,
    departmentId?: number,
    roleId?: number,
    options?: { skipLoader?: boolean },
  ): Observable<PeopleSdk.PagedEmployeeHierarchyResponse> {
    const skipLoader = options?.skipLoader === true;

    if (!skipLoader) {
      this.appEvents.loaderVisiblePublish(true, 'people-hierarchies-list');
    }

    const source$ = this.shouldMock('getAll')
      ? of(employeeHierarchiesSdkMock.getAllEmployeeHierarchies(page, size, locationId, departmentId, roleId)).pipe(delay(360))
      : this.employeeHierarchiesApi.getAllEmployeeHierarchies(page, size, locationId, departmentId, roleId);

    return source$.pipe(
      map((resp) => resp),
      catchError((err) => {
        this.showErrorToast(err);
        return throwError(() => err);
      }),
      finalize(() => {
        if (!skipLoader) {
          this.appEvents.loaderVisiblePublish(false, 'people-hierarchies-list');
        }
      }),
    );
  }

  getEmployeesByManagerId(
    managerId: number,
    page?: number,
    size?: number,
    locationId?: number,
    departmentId?: number,
    roleId?: number,
    options?: { skipLoader?: boolean },
  ): Observable<PeopleSdk.PagedEmployeeByManagerResponse> {
    const skipLoader = options?.skipLoader === true;

    if (!skipLoader) {
      this.appEvents.loaderVisiblePublish(true, 'people-hierarchies-employees-by-manager');
    }

    const source$ = this.shouldMock('getEmployeesByManager')
      ? of(employeeHierarchiesSdkMock.getEmployeesByManagerId(
        managerId, page, size, locationId, departmentId, roleId)).pipe(delay(360))
      : this.employeeHierarchiesApi.getEmployeesByManagerId(
        managerId, page, size, locationId, departmentId, roleId);

    return source$.pipe(
      map((resp) => resp),
      catchError((err) => {
        this.showErrorToast(err);
        return throwError(() => err);
      }),
      finalize(() => {
        if (!skipLoader) {
          this.appEvents.loaderVisiblePublish(false, 'people-hierarchies-employees-by-manager');
        }
      }),
    );
  }

  createEmployeeHierarchy(payload: PeopleSdk.EmployeeHierarchyCreateRequest): Observable<void> {
    this.appEvents.loaderVisiblePublish(true, 'people-hierarchy-create');

    const source$ = this.shouldMock('create')
      ? of(employeeHierarchiesSdkMock.createEmployeeHierarchy(payload)).pipe(delay(360))
      : this.employeeHierarchiesApi.createEmployeeHierarchy(payload);

    return source$.pipe(
      map(() => void 0),
      catchError((err) => {
        this.showErrorToast(err);
        return throwError(() => err);
      }),
      finalize(() => this.appEvents.loaderVisiblePublish(false, 'people-hierarchy-create')),
    );
  }

  updateEmployeeHierarchy(
    employeeHierarchyId: number,
    payload: PeopleSdk.EmployeeHierarchyUpdateRequest,
  ): Observable<void> {
    this.appEvents.loaderVisiblePublish(true, 'people-hierarchy-update');

    const source$ = this.shouldMock('update')
      ? of(employeeHierarchiesSdkMock.updateEmployeeHierarchy(employeeHierarchyId, payload)).pipe(delay(360))
      : this.employeeHierarchiesApi.updateEmployeeHierarchy(employeeHierarchyId, payload);

    return source$.pipe(
      map(() => void 0),
      catchError((err) => {
        this.showErrorToast(err);
        return throwError(() => err);
      }),
      finalize(() => this.appEvents.loaderVisiblePublish(false, 'people-hierarchy-update')),
    );
  }

  deleteEmployeeHierarchyById(employeeHierarchyId: number): Observable<void> {
    this.appEvents.loaderVisiblePublish(true, 'people-hierarchy-delete');

    const source$ = this.shouldMock('delete')
      ? of(employeeHierarchiesSdkMock.deleteEmployeeHierarchyById(employeeHierarchyId)).pipe(delay(360))
      : this.employeeHierarchiesApi.deleteEmployeeHierarchyById(employeeHierarchyId);

    return source$.pipe(
      map(() => void 0),
      catchError((err) => {
        this.showErrorToast(err);
        return throwError(() => err);
      }),
      finalize(() => this.appEvents.loaderVisiblePublish(false, 'people-hierarchy-delete')),
    );
  }

  private shouldMock(action: EmployeeHierarchiesMockAction): boolean {
    return Boolean(this.env?.useMocks || this.env?.mock?.employeeHierarchies?.[action]);
  }

  private showErrorToast(err: any) {
    if (!this.toast) return;

    const status = err?.status;
    const errorCode = err?.error?.code;
    const errorDescription = String(err?.error?.description ?? '');

    if (status === 400 && errorCode === 'KLAB400' && errorDescription.toLowerCase().includes('cycle')) {
      this.toast.show({
        data: {
          type: 'info',
          title: 'Jerarquía no permitida',
          msg: 'No puedes asignar ese empleado porque ya pertenece a la cadena superior del líder seleccionado.',
          direction: CoreUIToastDirection.topCenter,
          autoClose: false,
          closable: true,
        },
      });
      return;
    }

    if (status === 409 && errorCode === 'KLAB409') {
      const isDeleteWithAssignedEmployees =
        errorDescription.toLowerCase().includes('assigned employees');
      this.toast.show({
        data: {
          type: 'info',
          title: isDeleteWithAssignedEmployees ? 'Acción no permitida' : 'Empleado ya registrado',
          msg: isDeleteWithAssignedEmployees
            ? 'No puedes retirar este líder porque tiene empleados asignados.'
            : 'Este empleado ya forma parte de una jerarquía organizacional.',
          direction: CoreUIToastDirection.topCenter,
          autoClose: false,
          closable: true,
        },
      });
      return;
    }

    const mapMsg: Record<number, string> = {
      400: 'Solicitud inválida',
      401: 'No autorizado',
      404: 'No encontrado',
      409: 'Registro duplicado',
      429: 'Demasiadas solicitudes',
      500: 'Error interno del servidor',
    };

    this.toast.show({
      data: {
        title: 'Error',
        msg: mapMsg[status] || 'Error inesperado',
        direction: CoreUIToastDirection.topCenter,
        autoClose: true,
      },
    });
  }
}
